/**
 * Extract a short water-stress tier for KPI chips from authored prose.
 * Cover chips need "Medium-High" — not truncated narrative openings like
 * "According to the WRI Aqueduct Water Risk Atla…".
 */

const KNOWN_TIERS = [
  'Extremely High',
  'Medium-High',
  'Low-Medium',
  'Moderate',
  'Medium',
  'High',
  'Low',
] as const;

const SENTENCE_OPENERS = /^(according|the |this |based on|under |in accordance)/i;

function normalizeTierLabel(label: string): string {
  const trimmed = label.trim().replace(/\s+/g, ' ');
  for (const tier of KNOWN_TIERS) {
    const re = new RegExp(`^(${tier.replace('-', '[- ]')})(?:\\s*(\\([^)]{1,24}\\)))?`, 'i');
    const match = trimmed.match(re);
    if (match) {
      return match[2] ? `${tier} ${match[2]}` : tier;
    }
  }
  return trimmed.length > 32 ? `${trimmed.slice(0, 29).trimEnd()}…` : trimmed;
}

function scanKnownTier(text: string): string | null {
  for (const tier of KNOWN_TIERS) {
    const re = new RegExp(`\\b(${tier.replace('-', '[- ]')})(?:\\s*(\\([^)]{1,24}\\)))?`, 'i');
    const match = text.match(re);
    if (match) {
      return match[2] ? `${tier} ${match[2]}` : tier;
    }
  }
  return null;
}

/**
 * Returns a concise chip label or `null` when prose cannot be parsed.
 */
export function parseWaterStressChipLabel(raw: string | undefined | null): string | null {
  const text = raw?.trim();
  if (!text) return null;

  if (text.length <= 28 && !SENTENCE_OPENERS.test(text) && !text.includes('.')) {
    return normalizeTierLabel(text);
  }

  const ratedAs = text.match(
    /(?:baseline\s+)?water\s+stress[^.\n]{0,96}?(?:as|is|rated|at|of)\s+([A-Za-z][A-Za-z\s-]*(?:\([^)]{1,24}\))?)/i,
  );
  if (ratedAs?.[1]) {
    const parsed = scanKnownTier(ratedAs[1]) ?? normalizeTierLabel(ratedAs[1]);
    if (parsed) return parsed;
  }

  const aqueductColon = text.match(/WRI\s+Aqueduct[^:.\n]*:\s*([^.,;\n]+)/i);
  if (aqueductColon?.[1]) {
    const parsed = scanKnownTier(aqueductColon[1]);
    if (parsed) return parsed;
  }

  const tier = scanKnownTier(text);
  if (tier) return tier;

  if (text.length <= 32 && !SENTENCE_OPENERS.test(text)) {
    return normalizeTierLabel(text);
  }

  return null;
}
